import { useState } from 'react'
import { useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { parseError } from '@/lib/errors'
import type { CoverageType, EnabledCoverage } from '@/models/insuranceCoverage'
import { CoverageNeedsFields } from './CoverageNeedsFields'
import { useCoverageNeedsForm } from './useCoverageNeedsForm'

interface EditCoverageNeedsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  startDate: string | null
  selected: CoverageType[]
  orgId: string
  /** The coverages this application can choose; trucking's when not given. */
  menu?: ReadonlyArray<EnabledCoverage>
}

export function EditCoverageNeedsDialog({
  open,
  onOpenChange,
  onSuccess,
  startDate,
  selected,
  orgId,
  menu,
}: EditCoverageNeedsDialogProps) {
  const [loading, setLoading] = useState(false)
  const editor = useCoverageNeedsForm({ startDate, selected, menu })
  const [date, picked] = useWatch({ control: editor.form.control, name: ['startDate', 'coverages'] })

  function close() {
    editor.discard()
    onOpenChange(false)
  }

  async function handleSubmit() {
    if (!date) return
    setLoading(true)
    try {
      const result = await editor.save(orgId, { onlyChanged: false })
      if (!result.ok) {
        if (result.error) toast.error(parseError(result.error))
        return
      }
      onSuccess()
      toast.success('Coverage needs updated')
      onOpenChange(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(value) => (value ? onOpenChange(true) : close())}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Edit coverage needs</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <CoverageNeedsFields form={editor.form} menu={menu} />
        </div>
        <DialogFooter className="mt-6">
          <Button type="button" variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !date || !picked.length}
            loading={loading ? 'Saving...' : undefined}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
